import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { theme } from '../styles/theme';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Modal from '../components/common/Modal';
import DataTable from '../components/common/DataTable';
import { emailConfigAPI } from '../utils/api';
import { FaRegEdit } from "react-icons/fa";
import { FaRegTrashAlt } from "react-icons/fa";

const Container = styled.div`
  padding: ${theme.spacing.xl};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h1`
  margin: 0;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: ${theme.borderRadius.round};
  font-size: 0.8rem;
  color: ${props => (props.active ? '#0f5132' : '#842029')};
  background-color: ${props => (props.active ? '#d1e7dd' : '#f8d7da')};
  border: 1px solid ${props => (props.active ? '#badbcc' : '#f5c2c7')};
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const EmailConfigs = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [currentEdit, setCurrentEdit] = useState(null);

  const [createForm, setCreateForm] = useState({ email: '', password: '' });
  const [editForm, setEditForm] = useState({ email: '', password: '', isActive: false });

  const headers = useMemo(() => ['#', 'Email', 'Active', 'Actions'], []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await emailConfigAPI.getAll();
      setConfigs(Array.isArray(data) ? data : data?.data || []);
    } catch (e) {
      setError(e?.message || 'Failed to load email configurations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  const handleOpenCreate = () => {
    setCreateForm({ email: '', password: '' });
    setIsCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!createForm.email || !createForm.password) return;
    try {
      setLoading(true);
      await emailConfigAPI.create(createForm);
      setIsCreateOpen(false);
      await loadConfigs();
    } catch (e) {
      setError(e?.message || 'Failed to create email config');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = (row) => {
    setCurrentEdit(row);
    setEditForm({
      email: row.email || '',
      password: '',
      isActive: !!row.isActive,
    });
    setIsEditOpen(true);
  };

  const handleEdit = async () => {
    if (!currentEdit) return;
    const updates = {
      email: editForm.email,
      isActive: editForm.isActive,
    };
    if (editForm.password) updates.password = editForm.password;
    try {
      setLoading(true);
      await emailConfigAPI.update(currentEdit.id, updates);
      setIsEditOpen(false);
      setCurrentEdit(null);
      await loadConfigs();
    } catch (e) {
      setError(e?.message || 'Failed to update email config');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this email configuration?')) return;
    try {
      setLoading(true);
      await emailConfigAPI.remove(id);
      await loadConfigs();
    } catch (e) {
      setError(e?.message || 'Failed to delete email config');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Email Configurations</Title>
        <Button onClick={handleOpenCreate}>Add New Email</Button>
      </Header>

      {error && (
        <div className="alert alert-danger mt-3" role="alert">
          {error}
        </div>
      )}

      {loading && (
        <div className="mt-3">Loading...</div>
      )}

      <DataTable
        headers={headers}
        data={configs}
        renderRow={(row, index) => (
          <tr key={row.id}>
            <td>{index + 1}</td>
            <td>{row.email}</td>
            <td>
              <Badge className="rounded-4" active={!!row.isActive}>{row.isActive ? 'Active' : 'Inactive'}</Badge>
            </td>
            <td>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button size="small" variant="outlined" onClick={() => handleOpenEdit(row)}><FaRegEdit /></Button>
                <Button size="small" color="error" variant="outlined" onClick={() => handleDelete(row.id)}><FaRegTrashAlt /></Button>
              </div>
            </td>
          </tr>
        )}
      />

      <Modal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Add Email Config"
        onSubmit={handleCreate}
        submitLabel="Create"
      >
        <Input
          label="Email"
          placeholder="name@example.com"
          type="email"
          value={createForm.email}
          onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
        />
        <Input
          label="Password"
          placeholder="Password"
          type="password"
          value={createForm.password}
          onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
        />
      </Modal>

      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Update Email Config"
        onSubmit={handleEdit}
        submitLabel="Save"
      >
        <Input
          label="Email"
          type="email"
          value={editForm.email}
          onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
        />
        <Input
          label="Password (leave blank to keep)"
          type="password"
          value={editForm.password}
          onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
        />
        <CheckboxRow>
          <input
            type="checkbox"
            checked={!!editForm.isActive}
            onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
          />
          <span>Active</span>
        </CheckboxRow>
      </Modal>
    </Container>
  );
};

export default EmailConfigs;


